import { KeywordSources } from '@aibees/elephant/feature/search-table-company/hooks/types';
import { SearchMatchType } from '@aibees/elephant/types';
import { ComingSoonTag, Icon, IconType } from '@aibees/shared/components';
import { capitalize, fHumanReadableNumber } from '@aibees/shared/utils';
import { Box, Button, Checkbox, Tooltip, Typography } from '@mui/material';
import { mergeSx } from 'merge-sx';
import { memo, useCallback, useMemo } from 'react';
import { SearchFilterOption } from '../..';
import { SearchMatchButton } from '../../components/search-match-button';
import { SEARCH_MATCH_BUTTONS_CONFIG } from '../filter-exact-match/config';
import * as SX from './sx';
import { CheckboxItemProps } from './types';
import { useKeywordsSourceSearchMatchSelected } from './use-exact-match-checked';
import { buildKeywordsSearchMatchDraft } from './utils';

// Map for O(1) lookup in render instead of .find() per item
const SEARCH_MATCH_BUTTONS_MAP = new Map(
  SEARCH_MATCH_BUTTONS_CONFIG.map((c) => [c.type, c])
);
const POPPER_PROPS_DISABLE_PORTAL = { disablePortal: true };

export const CheckboxItem = memo(
  ({
    option,
    handleClick,
    config,
    metadata,
    table,
    includeMetadata,
    isFilterDisabled,
    handleOnlyClick,
    searchMatchParamsKey,
    searchMatchParamValue,
    onSetParam,
    onRemoveParam,
    labelSx,
  }: CheckboxItemProps) => {
    const { type: selectedType, isExistInParams } =
      useKeywordsSourceSearchMatchSelected(
        option.value as KeywordSources,
        searchMatchParamValue
      );

    const handleChangeSearchMatch = useCallback(
      (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        searchType: SearchMatchType
      ) => {
        event.stopPropagation();
        if (!option.checked) return;

        // Build the draft using the param value directly
        const draft = buildKeywordsSearchMatchDraft(
          option.value as KeywordSources,
          { [searchMatchParamsKey]: searchMatchParamValue } as never,
          searchType,
          searchMatchParamsKey
        );
        if (draft.length) onSetParam?.(searchMatchParamsKey, draft);
        else onRemoveParam?.(searchMatchParamsKey);
      },
      [
        option.value,
        option.checked,
        searchMatchParamValue,
        searchMatchParamsKey,
        onSetParam,
        onRemoveParam,
      ]
    );

    const itemSx = useMemo(
      () => mergeSx(SX.checkboxItem, metadata?.checkbox?.itemSx),
      [metadata?.checkbox?.itemSx]
    );

    const handleBoxClick = useCallback(() => {
      if (!option.disabled && !option.comingSoon) {
        handleClick(option.value, !option.checked, option.metadata);
      }
    }, [
      option.disabled,
      option.comingSoon,
      option.value,
      option.checked,
      option.metadata,
      handleClick,
    ]);

    const labelKey = config?.labelKey as keyof SearchFilterOption | undefined;
    const label =
      (labelKey ? (option[labelKey] as string) : undefined) ?? option.value;

    const tooltipTitle =
      isFilterDisabled && table ? metadata?.availability?.info?.[table] : null;

    return (
      <Box
        className="CheckboxItem"
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        borderRadius={1}
        sx={itemSx}
        onClick={handleBoxClick}
        key={option.value}
      >
        <Box display={'flex'} alignItems={'center'} gap={0.25}>
          <Checkbox
            disabled={option.disabled || option.comingSoon}
            checked={option.checked}
            indeterminate={option.indeterminate}
            name={option.value}
            id={label}
            size="small"
          />
          <Tooltip
            PopperProps={POPPER_PROPS_DISABLE_PORTAL}
            title={tooltipTitle}
            arrow
            placement="top"
          >
            <Box display={'flex'} alignItems={'center'} gap={0.5}>
              {option.icon && (
                <Icon
                  size={20}
                  name={{
                    src: option.icon,
                    sx: SX.labelIcon(option.checked),
                  }}
                />
              )}
              <Typography
                color="grey.600"
                variant="body4"
                sx={labelSx}
                children={label}
              />
            </Box>
          </Tooltip>
          {option.value !== 'select-all' &&
            metadata?.checkbox?.showOnlyButton && (
              <Button
                variant="text"
                size="xsmall"
                color="primary"
                className="only-button"
                sx={SX.onlyButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOnlyClick?.(option.value);
                }}
              >
                Only
              </Button>
            )}
        </Box>
        <Box display={'flex'} alignItems={'center'}>
          {includeMetadata && !option.comingSoon && (
            <Typography variant="body5" color="grey.600">
              {fHumanReadableNumber(option.count)}
            </Typography>
          )}
          {option.comingSoon && <ComingSoonTag />}
          {!option.comingSoon &&
            !includeMetadata &&
            option.metadata?.searchMatchItems?.length && (
              <Box
                display="flex"
                gap={0.5}
                marginRight={-0.5}
                onClick={(e) => e.stopPropagation()}
              >
                {option.metadata?.searchMatchItems?.map((item) => {
                  const buttonConfig = SEARCH_MATCH_BUTTONS_MAP.get(item);
                  return (
                    <SearchMatchButton
                      key={option.value + item}
                      type={item}
                      label={`Search ${capitalize(item)} Match`}
                      icon={buttonConfig?.icon as IconType}
                      selected={
                        option.checked &&
                        (selectedType === item ||
                          (!isExistInParams &&
                            item === option.metadata?.searchMatchDefault))
                      }
                      disabled={option.metadata?.disabledSearchMatchItems?.includes(
                        item
                      )}
                      handleChange={handleChangeSearchMatch}
                    />
                  );
                })}
              </Box>
            )}
        </Box>
      </Box>
    );
  }
);

CheckboxItem.displayName = 'CheckboxItem';
